/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BasicTable from '@home/components/BasicTable';
import { SortBy } from 'tea-component/lib/table/addons';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import getRenderMap from './getRender';
import classNames from 'classnames';
import { useSelector } from 'umi';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';
import './index.scss';

const KolTable = (props: any) => {
  const {
    style,
    className,
    componentMetrics,
    tableData,
    params,
    loading,
    isError,
    tableCount,
    setParams,
    defaultSort,
    getSecTableData,
    filterVal,
    defaultFixedColumns,
    stickyHeader
  } = props || {};

  const explainInfo = useSelector((state: any) => state.studio?.explainInfo);

  const [sorts, setSorts] = useState<SortBy[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const oldExpandKeys = useRef<string[]>([]);

  // 点查询重置
  const resetExpand = () => {
    setExpandedKeys([]);
    oldExpandKeys.current = [];
  };

  useEffect(() => {
    resetExpand();
    setSorts([]);
  }, [filterVal]);

  const columns = useMemo(() => {
    return componentMetrics?.map((item: any) => {
      const { renderMap, ...otherOptions } = item;
      let render;
      if (getRenderMap[renderMap]) {
        render = (row: any, col: any) => {
          const { valueFormat, metricKey } = col || {};
          // Total行不需要做特殊处理
          if (row?.campaign_name === 'TOTAL') {
            if (valueFormat) {
              return dataFormat({
                value: dynamicString(metricKey, row),
                formatRule: valueFormat,
              });
            }
            return row[metricKey] || '-';
          }
          return getRenderMap[renderMap]?.(row, item);
        };
      }
      return {
        render,
        ...otherOptions,
      };
    });
  }, [componentMetrics]);

  // 展开
  const onExpandedChange = (keys: string[]) => {
    // 获取当前选中项
    let currentKey;
    if (keys.length > oldExpandKeys.current?.length) {
      currentKey = keys?.find((key) => !oldExpandKeys.current?.includes(key));
    } else {
      currentKey = oldExpandKeys.current?.find((key) => !keys.includes(key));
    }
    setExpandedKeys(keys);
    oldExpandKeys.current = keys;
    getSecTableData(currentKey, tableData);
  };

  // 排序
  const onSortChange = (sorts: SortBy[]) => {
    let newSorts = sorts;
    if (newSorts.length === 0) {
      newSorts = defaultSort || [];
    }
    const newParams = {
      ...params,
      order_by: newSorts?.map((item) => ({ by: item?.by, order: item?.order })),
    };
    setSorts(sorts.length === 0 ? sorts : newSorts);
    setParams?.(newParams);
    resetExpand();
  };

  // 切换分页
  const onPagingChange = (paging: any) => {
    const { pageIndex, pageSize } = paging || {};
    const newParams = {
      ...params,
      page_index: pageIndex,
      page_size: pageSize,
    };
    setParams?.(newParams);
    resetExpand();
  };

  const expandConfig = useMemo(() => {
    return {
      expandedKeys,
      onExpandedChange,
    };
  }, [expandedKeys, params, tableData, loading]);

  const sorterConfig = useMemo(() => {
    return {
      sorts,
      onSortChange,
    };
  }, [sorts, params]);

  const paginationConfig = useMemo(() => {
    return {
      pageIndex: params?.page_index,
      pageSize: params?.page_size,
      onPagingChange,
    };
  }, [params]);

  const getRowClassName = (row: any) => {
    if (row.isChildren) {
      return 'dbh-kol-table-children-row';
    }
  };

  return (
    <CardTitleBox
      className={classNames(className, 'dbh-kol-table-container')}
      style={style}
      normalConfig={{
        isLoading: false,
        isError,
        isNoData: tableData?.length === 0,
        componentLoading: loading,
      }}
    >
      <BasicTable
        columns={columns}
        expandConfig={expandConfig}
        sorterConfig={sorterConfig}
        records={tableData}
        hasFixedColoum
        fixedColumnList={defaultFixedColumns}
        recordCount={tableCount}
        paginationConfig={paginationConfig}
        explainInfo={explainInfo}
        rowClassName={getRowClassName}
        stickyHeader={stickyHeader}
      />
    </CardTitleBox>
  );
};

export default KolTable;
